import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { StatusBadge, Overline } from "@/components/ui-bits";
import { toast } from "sonner";
import { ShoppingCart, Plus, CreditCard, X, Loader2, Repeat, Package, Link2, Check } from "lucide-react";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);
  const [linking, setLinking] = useState(null);
  const [copied, setCopied] = useState(null);

  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/agents").then((r) => setAgents(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    const i = setInterval(load, 12000);
    return () => clearInterval(i);
  }, []);
  useRealtime(() => { load(); api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}); });

  const checkout = async (order) => {
    setCheckingOut(order.id);
    try {
      const { data } = await api.post(`/orders/${order.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setCheckingOut(null);
    }
  };

  const mintLink = async (order) => {
    setLinking(order.id);
    try {
      const { data } = await api.post(`/orders/${order.id}/payment-link`);
      try {
        await navigator.clipboard.writeText(data.url);
        setCopied(order.id);
        setTimeout(() => setCopied(null), 2500);
      } catch { /* clipboard may be blocked; link still returned */ }
      toast.success("Payment link ready — copied to clipboard", { description: "Send it to the lead. Revenue is attributed to the selling agent on payment." });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create payment link");
    } finally { setLinking(null); }
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Overline>Orders & Autonomous Revenue</Overline>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Orders & Revenue</h1>
        </div>
        <button data-testid="new-order-btn" onClick={() => setShowNew(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 transition-colors">
          <Plus className="w-4 h-4" /> Draft Order
        </button>
      </div>

      <div className="hud-card rounded-lg p-5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <th className="pb-2 font-medium">Product</th><th className="pb-2 font-medium">Customer</th>
                <th className="pb-2 font-medium">Agent</th><th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium text-right">Amount</th><th className="pb-2 font-medium text-center">Status</th>
                <th className="pb-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-600 font-mono text-xs">No orders yet. Draft one or simulate a sale from the console.</td></tr>}
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-800/60" data-testid={`order-row-${o.id}`}>
                  <td className="py-3 font-medium flex items-center gap-2">
                    {o.order_type === "subscription" ? <Repeat className="w-3.5 h-3.5 text-cyan-400" /> : <Package className="w-3.5 h-3.5 text-slate-500" />}
                    {o.product_name}
                  </td>
                  <td className="py-3 text-slate-400">{o.customer_name}</td>
                  <td className="py-3 text-slate-400 font-mono text-xs">{o.agent_name}</td>
                  <td className="py-3 text-slate-500 font-mono text-[11px] uppercase">{o.order_type.replace("_", " ")}</td>
                  <td className="py-3 text-right font-mono tabular-nums text-emerald-400">{money(o.amount)}</td>
                  <td className="py-3 text-center"><StatusBadge status={o.status} /></td>
                  <td className="py-3 text-right">
                    {(o.status === "draft" || o.status === "pending_payment") && (
                      <div className="inline-flex items-center gap-1.5">
                        <button data-testid={`payment-link-${o.id}`} onClick={() => mintLink(o)} disabled={linking === o.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-500/15 border border-cyan-500/40 text-cyan-400 font-mono text-[10px] uppercase tracking-wider hover:bg-cyan-500/25 transition-colors disabled:opacity-60"
                          title="Mint a shareable payment link an agent can send to the lead">
                          {linking === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : copied === o.id ? <Check className="w-3 h-3" /> : <Link2 className="w-3 h-3" />}
                          {copied === o.id ? "Copied" : "Payment Link"}
                        </button>
                        <button data-testid={`checkout-${o.id}`} onClick={() => checkout(o)} disabled={checkingOut === o.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 font-mono text-[10px] uppercase tracking-wider hover:bg-emerald-500/25 transition-colors disabled:opacity-60">
                          {checkingOut === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />} Checkout
                        </button>
                      </div>
                    )}
                    {o.status === "paid" && o.payment_link_url && (
                      <span className="font-mono text-[10px] text-emerald-400/70 uppercase tracking-wider">Paid</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && <NewOrder agents={agents.filter((a) => a.status !== "killed")} customers={customers} onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewOrder({ agents, customers, onClose, onDone }) {
  const [f, setF] = useState({ customer_id: customers[0]?.id || "", agent_id: agents[0]?.id || "", product_name: "", order_type: "one_time", amount: 149 });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_id || !f.agent_id) { toast.error("Add a customer and an agent first"); return; }
    setLoading(true);
    try {
      await api.post("/orders", { ...f, amount: Number(f.amount) });
      toast.success("Order drafted");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  const Fld = ({ label, testid, options, children, ...rest }) => (
    <div>
      <label className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400 mb-1 block">{label}</label>
      {children}
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md hud-card rounded-xl p-6 bg-[#0E131F]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-lg">Draft Order</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Fld label="Customer"><select data-testid="order-customer-select" value={f.customer_id} onChange={set("customer_id")} className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm">{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Fld>
          <Fld label="Selling Agent"><select data-testid="order-agent-select" value={f.agent_id} onChange={set("agent_id")} className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm">{agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Fld>
          <Fld label="Product / Service"><input data-testid="order-product-input" value={f.product_name} onChange={set("product_name")} required placeholder="Landing Page Bundle" className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60" /></Fld>
          <div className="grid grid-cols-2 gap-2">
            <Fld label="Type"><select data-testid="order-type-select" value={f.order_type} onChange={set("order_type")} className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm"><option value="one_time">One-time</option><option value="subscription">Subscription</option></select></Fld>
            <Fld label="Amount ($)"><input data-testid="order-amount-input" type="number" value={f.amount} onChange={set("amount")} className="w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60" /></Fld>
          </div>
          <button type="submit" data-testid="order-submit-btn" disabled={loading} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Draft"}
          </button>
        </form>
      </div>
    </div>
  );
}
